import React, { createContext, useState, useEffect, useContext } from "react";
import { BrowserProvider } from "ethers";

// 🌐 Context global Web3
export const Web3Context = createContext(null);

export const Web3Provider = ({ children }) => {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  // === Connexion MetaMask ===
  const connectWallet = async () => {
    if (!window.ethereum) {
      alert("🦊 Please install MetaMask!");
      return;
    }

    try {
      const browserProvider = new BrowserProvider(window.ethereum);
      const accounts = await browserProvider.send("eth_requestAccounts", []);
      if (!accounts.length) throw new Error("No wallet connected.");

      const signer = await browserProvider.getSigner();
      const address = await signer.getAddress();
      const network = await browserProvider.getNetwork();

      setProvider(browserProvider);
      setSigner(signer);
      setAccount(address);
      setChainId(Number(network.chainId));
      setIsConnected(true);

      localStorage.setItem("walletConnected", "true");
      localStorage.setItem("walletAddress", address);

      return address;
    } catch (err) {
      console.error("Error connecting wallet:", err);
      if (err.code === 4001) alert("Wallet connection rejected.");
    }
  };

  // === Déconnexion ===
  const disconnectWallet = () => {
    setProvider(null);
    setSigner(null);
    setAccount(null);
    setChainId(null);
    setIsConnected(false);
    localStorage.removeItem("walletConnected");
    localStorage.removeItem("walletAddress");
  };

  // === Reconnexion automatique ===
  useEffect(() => {
    const restoreConnection = async () => {
      if (window.ethereum && localStorage.getItem("walletConnected")) {
        try {
          const browserProvider = new BrowserProvider(window.ethereum);
          const accounts = await browserProvider.listAccounts();
          if (accounts.length > 0) {
            const signer = await browserProvider.getSigner();
            const address = await signer.getAddress();
            const network = await browserProvider.getNetwork();

            setProvider(browserProvider);
            setSigner(signer);
            setAccount(address);
            setChainId(Number(network.chainId));
            setIsConnected(true);
          }
        } catch (err) {
          console.error("Auto-connect failed:", err);
        }
      }
    };

    restoreConnection();

    // === Gestion des événements MetaMask ===
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length === 0) disconnectWallet();
        else restoreConnection();
      };

      const handleChainChanged = async (chainIdHex) => {
        const newId = parseInt(chainIdHex, 16);
        setChainId(newId);
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
        window.ethereum.removeListener("chainChanged", handleChainChanged);
      };
    }
  }, []);

  const value = {
    provider,
    signer,
    account,
    chainId,
    isConnected,
    connectWallet,
    disconnectWallet,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

// === Hook personnalisé ===
export const useWeb3 = () => useContext(Web3Context);
